// useChainEditing.ts

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CHAIN_FIELDS, recomputeChain } from '../../../Utils/measurementChain';
import {
  applyChainUpdates,
  collectChainSnapshot,
  findEditedChainValue,
} from '../../../Utils/chainFieldSync';
import { backfillChainMarks } from '../../../Utils/chainFieldBackfill';
import { backfillAortaSlot } from '../../../Utils/aortaSlot';
import {
  applyRapFromPhrase,
  backfillTricuspidPressure,
  isRapPhrasePosition,
  syncRapPhrase,
} from '../../../Utils/tricuspidPressure';
import {
  joinValueAndUnit,
  resolveUnit,
  splitValueAndUnit,
  CHAIN_EMPTY_MARKER,
} from '../../../Utils/measurementValueParts';
import { evaluateWordingRules } from '../../../Utils/wordingRules';
import { resolvePhrase } from '../../../Utils/wordingVocabulary';
import {
  applyPhraseUpdates,
  collectPhraseSnapshot,
  groupPhraseOccurrences,
  isPhraseSlotLocked,
  markPhraseUserOwned,
} from '../../../Utils/phraseSlotSync';
import {
  ARRHYTHMIA_SLOT,
  resolveArrhythmiaPhrase,
  type DetectedArrhythmia,
} from '../../../Utils/arrhythmiaVocabulary';
import {
  CHAIN_POPUP_WIDTH,
  CHAIN_POPUP_HEIGHT,
  getReportPopupPosition,
  ReportPopupPosition,
} from './reportPopupPosition';

/**
 * Field chaining and smart wording, bound to one TipTap editor.
 *
 * Extracted on 1 Sep for the reports page (`/patientReports/:_id`), which renders its own
 * editor rather than `ReportsLayout` and so had none of this: no marks, nothing clickable,
 * no self-writing sentences. Nikhil, 1 Sep: *"in studies page the smart reporting works /
 * in reports page its not working please fix it"*.
 *
 * WHY THIS WORKS WITHOUT THE DICOM SR. `ReportsLayout` gets its measurements by parsing
 * the study's SR out of OHIF's metadata store, which needs the viewer to have loaded the
 * study — and `/patientReports/:_id` has no study loaded at all. It does not matter:
 * `backfillChainMarks` finds the values by their LABEL in the saved document, and
 * `collectChainSnapshot` reads them straight back out of it. The report body is the source
 * of truth. `seedValues` below is only a fallback for anything the body does not print.
 *
 * The consequence, and it is worth being clear about: a measurement that is not printed in
 * the report cannot be chained from this page, and a report whose template predates the
 * span migration has fewer labelled anchors, so fewer fields participate. That is a
 * property of the document, not a bug to chase.
 *
 * `ReportsLayout` still carries its own copy of this wiring. It was left alone
 * deliberately — it is the surface clinicians use every day and it works. Migrating it
 * onto this hook is a follow-up, and the reason this was written as a hook rather than
 * copied a second time.
 */

export type ChainSlot = {
  from: number;
  to: number;
  key: string;
  label: string;
  /** The whole printed value ("4.8 cm") — what a no-change commit compares against. */
  originalValue: string;
  /** Fixed unit, shown beside the input and reattached on commit. Never edited. */
  unit: string;
  /** The value's own number/unit separator, reused so an unedited value is unchanged. */
  separator: string;
};

export type ChainNotice = {
  changed: string[];
  warnings: string[];
};

type UseChainEditingArgs = {
  /**
   * Ref to the editor holding the report body.
   *
   * A ref and not the editor itself, deliberately. The caller has to call this hook
   * BEFORE `useEditor`, because the editor's `handleClick` closes over these handlers when
   * it is created. Mirroring the editor into state to get it back here introduced a race:
   * `useEditor` returns null on the first render, and the content-loading effect is async,
   * so the priming pass could run while the state copy was still null and silently do
   * nothing. A ref assigned straight after `useEditor` is always current by the time any
   * of these callbacks fire.
   */
  editorRef: { current: any };
  /**
   * Values to fall back on for anything the report body does not print — typically
   * `sex` for the wording rules. The document always wins over these.
   */
  seedValues?: Record<string, unknown>;
  /** Patient sex, for the sex-specific wording thresholds. */
  sex?: unknown;
  /**
   * Atrial fibrillation mark for this study. The diastolic rule stays silent in AF —
   * E/A means nothing without an A wave.
   */
  atrialFibrillation?: boolean;
  /** This clinic's phrase lists, keyed by slot. A rule with no vocabulary stays quiet. */
  dropdownMap?: Record<string, string[]>;
  /**
   * The arrhythmia the study's CardioSoft export recorded, if it recorded one, as
   * detected by `v2/helper/stressArrhythmia.js`. `null` means the export said nothing
   * about rhythm — which is NOT the same as it saying there were none, so the slot is
   * left exactly as the template wrote it.
   */
  arrhythmia?: DetectedArrhythmia | null;
  /** False on a report this clinician may not write to (Finalized, not theirs). */
  canWrite: boolean;
  /**
   * Called after the chain or the wording rules have changed the document, with the
   * document AS IT SHOULD BE SAVED. These writes are the clinician's edit propagated, so
   * they do belong in the report.
   */
  onDocumentChanged?: () => void;
};

export function useChainEditing({
  editorRef,
  seedValues,
  sex,
  atrialFibrillation = false,
  dropdownMap,
  arrhythmia = null,
  canWrite,
  onDocumentChanged,
}: UseChainEditingArgs) {
  const [chainSlot, setChainSlot] = useState<ChainSlot | null>(null);
  const [chainPosition, setChainPosition] = useState<ReportPopupPosition | null>(null);
  const [chainDraft, setChainDraft] = useState('');
  const [chainNotice, setChainNotice] = useState<ChainNotice | null>(null);

  /** Last known `key -> value` snapshot. The typing-driven chain diffs against this. */
  const chainValuesRef = useRef<Record<string, string> | null>(null);
  /**
   * True while the chain itself is writing to the document — marking values on open, or
   * applying recomputed ones. The caller reads this to keep those writes out of its dirty
   * flag and out of autosave, so merely OPENING a report never saves it.
   */
  const chainWritingRef = useRef(false);
  /**
   * Ownership of a phrase slot lives in the DOCUMENT (`data-user` / `data-auto` on the
   * span), not in a ref here. A ref cannot survive a save, and that is precisely how a
   * clinician's correction came back on the next open looking like an untouched template
   * default and was overwritten — see `isPhraseSlotLocked`.
   */

  // Read through refs: these are rebuilt on every render of the calling page, and
  // depending on them directly would re-run a full document walk for no change.
  const seedRef = useRef(seedValues);
  seedRef.current = seedValues;
  const dropdownRef = useRef(dropdownMap);
  dropdownRef.current = dropdownMap;
  const canWriteRef = useRef(canWrite);
  canWriteRef.current = canWrite;
  const onChangedRef = useRef(onDocumentChanged);
  onChangedRef.current = onDocumentChanged;
  const sexRef = useRef(sex);
  sexRef.current = sex;
  const afRef = useRef(atrialFibrillation);
  afRef.current = atrialFibrillation;
  const arrhythmiaRef = useRef(arrhythmia);
  arrhythmiaRef.current = arrhythmia;

  const closeChainPopup = useCallback(() => {
    setChainPosition(null);
    setChainSlot(null);
    setChainDraft('');
  }, []);

  /**
   * Smart wording — pick each sentence's phrase from the measurements.
   *
   * The engine will not put words in a clinician's mouth. A slot is written only while it
   * still holds what the report opened with, or what this engine last wrote into it. The
   * moment a human changes it, the rule stops writing and raises a note instead.
   *
   * Returns true when the document was changed.
   */
  const runWordingRules = useCallback((activeEditor: any): boolean => {
    if (!activeEditor) {
      return false;
    }

    const phrases = collectPhraseSnapshot(activeEditor);
    const occurrencesBySlot = groupPhraseOccurrences(activeEditor);

    const updates: Record<string, string> = {};
    const notes: string[] = [];
    /**
     * Findings the engine worked out but deliberately did NOT write — the diastolic grade
     * and the filling-pressure call. Shown only alongside a change it actually made, so a
     * report that opens with nothing altered opens silently.
     */
    const advisoryNotes: string[] = [];

    /**
     * Offer one phrase for one slot — only while the slot is still the engine's to write.
     *
     * Ownership comes from the span's own attributes, so it survives a save and a reopen.
     * A locked slot is skipped in SILENCE: once a clinician owns a sentence, the engine
     * disagreeing with it is the report's normal permanent state, and announcing it on
     * every open is what made the notice something they had to dismiss all day.
     */
    const propose = (
      slot: string,
      phrase: string | null,
      reason: string,
      advisory = false
    ) => {
      if (!phrase) return; // no vocabulary for it — never invent wording
      const current = phrases[slot];
      if (current === undefined) return; // this template does not carry that sentence
      // Case-insensitive: `applyPhraseUpdates` cases a phrase to suit the position it
      // prints in, so the capitalised sentence-initial copy is the SAME phrase, not a
      // clinician's disagreement to be flagged on every pass.
      if (phrase.toLowerCase() === current.toLowerCase()) return;
      if (isPhraseSlotLocked(occurrencesBySlot[slot] || [])) return;
      if (advisory) {
        // Told, not written. The clinician decides whether it belongs in the report.
        advisoryNotes.push(`${reason} suggests "${phrase}" — for review, not applied.`);
        return;
      }

      updates[slot] = phrase;
      notes.push(`${reason} → "${phrase}"`);
    };

    /**
     * The arrhythmia line, filled from the study's own CardioSoft export.
     *
     * The one finding here that is IMPORTED rather than derived from measurements, which
     * is why it does not live in `wordingRules.ts`. Resolved against this clinic's own
     * option list, so nothing is written when the list cannot express it, and the
     * provenance goes in the note so a clinician can see it came from their machine.
     */
    const detected = arrhythmiaRef.current;
    if (detected) {
      propose(
        ARRHYTHMIA_SLOT,
        resolveArrhythmiaPhrase(detected, dropdownRef.current?.[ARRHYTHMIA_SLOT]),
        `CardioSoft report states "${detected.source}"`
      );
      // The slot holds one phrase, so findings past the worst are named rather than
      // silently dropped.
      if (detected.findings && detected.findings.length > 1) {
        notes.push(
          `CardioSoft also recorded ${detected.findings
            .slice(1)
            .map(f => f.replace(/_/g, ' '))
            .join(', ')} — add manually if required.`
        );
      }
    }

    const measurements = { ...(seedRef.current || {}), ...collectChainSnapshot(activeEditor) };
    const suggestions = evaluateWordingRules(measurements, sexRef.current, {
      atrialFibrillation: afRef.current === true,
    });

    for (const suggestion of suggestions) {
      propose(
        suggestion.slot,
        resolvePhrase(suggestion.intent, dropdownRef.current?.[suggestion.slot]),
        `${suggestion.reason}${suggestion.borderline ? ' (borderline)' : ''}`,
        suggestion.advisory === true
      );
    }

    // `true` = the engine's own words; stamps `data-auto` so a later open can tell them
    // from a clinician's edit.
    const written =
      Object.keys(updates).length > 0 ? applyPhraseUpdates(activeEditor, updates, true) : 0;

    // Only when the engine actually changed something — see the note on the same guard
    // in `ReportsLayout`. A settled report opens silently.
    if (written > 0 && (notes.length > 0 || advisoryNotes.length > 0)) {
      setChainNotice(previous => ({
        changed: previous?.changed || [],
        warnings: [...notes, ...advisoryNotes],
      }));
    }

    return written > 0;
  }, []);

  /**
   * Open the popup on a clicked chained value. Returns false when the click was not on
   * one, so the caller can carry on to its other handlers.
   */
  const openChainField = useCallback(
    (target: HTMLElement): boolean => {
      const editor = editorRef.current;
      if (!editor || !canWriteRef.current) {
        return false;
      }
      const chainKey = target.classList[0];
      if (!chainKey || !CHAIN_FIELDS[chainKey]) {
        return false;
      }

      const chainPos = editor.view.posAtDOM(target, 0);
      const currentChainValue = target.textContent || '';
      setChainNotice(null);
      // Only the figure is offered for editing; the unit is held aside and put back on
      // commit, so it cannot be mistyped or dropped.
      const parts = splitValueAndUnit(currentChainValue);
      // Freeform fields (EF_Prose) have no unit to split off, and splitting one out
      // anyway would truncate anything that happens to start with a digit — "45-60%,
      // mildly reduced" would otherwise open the popup showing only "45". The whole text
      // is the editable draft; a placeholder still opens empty.
      const isFreeformChainField = !!CHAIN_FIELDS[chainKey]?.freeform;
      const number =
        isFreeformChainField && currentChainValue.trim() !== CHAIN_EMPTY_MARKER
          ? currentChainValue.trim()
          : parts.number;
      // An empty cell has no unit of its own — take the field's, or the first value typed
      // into it is written bare.
      //
      // Freeform fields have no unit at all, ever — `resolveUnit` must not run for them.
      // It reads `parts.unit`, which comes from `splitValueAndUnit` treating the WHOLE
      // text as "<number><rest>": a value like "40-50%" starts with digits, so it split
      // into number "40" and unit "-50%" and the popup would show a phantom "-50%" label
      // beside the box — and worse, re-append it on commit if the typed draft ever
      // stopped ending in "-50%" ("60-70%" → "60-70%-50%").
      const { unit, separator } = isFreeformChainField
        ? { unit: '', separator: '' }
        : resolveUnit(parts, CHAIN_FIELDS[chainKey]?.unit);
      setChainDraft(number);
      setChainSlot({
        from: chainPos,
        to: chainPos + currentChainValue.length,
        key: chainKey,
        label: CHAIN_FIELDS[chainKey].label,
        // Rebuilt from the parts rather than the raw DOM text: EF's mark holds the figure
        // only, so `currentChainValue` is unit-less and would never equal a rejoined
        // "unchanged" commit, marking the report dirty for a field nobody touched.
        originalValue: joinValueAndUnit(number, unit, separator),
        unit,
        separator,
      });
      const reportElement = editor.view.dom.closest('.report-layout') as HTMLElement | null;
      setChainPosition(
        getReportPopupPosition(target, reportElement, CHAIN_POPUP_WIDTH, CHAIN_POPUP_HEIGHT)
      );
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Write a retyped measurement value back, then push it through the chain.
   *
   * The value map is the document's own chained values layered OVER the seed values, so a
   * value the clinician already corrected wins over anything supplied from outside.
   */
  const commitChainField = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !chainSlot || !canWriteRef.current) {
      return;
    }

    const { key, originalValue, unit, separator } = chainSlot;
    // The clinician typed a figure; the unit and its original separator come back from the
    // slot untouched, so an unedited value rebuilds byte-for-byte and the no-change guard
    // below still recognises it.
    const nextValue = joinValueAndUnit(chainDraft, unit, separator);

    // An empty value would collapse the span and lose the field from the report for good,
    // so a cleared input is treated as "no change".
    if (!nextValue || nextValue === originalValue) {
      closeChainPopup();
      return;
    }

    const scrollPosition = editor.view.dom.scrollTop;
    chainWritingRef.current = true;
    try {
      const documentValues = collectChainSnapshot(editor);
      const baseValues = { ...(seedRef.current || {}) };
      // A field such as EF is printed more than once. If the clicked copy is not the final
      // occurrence, the document snapshot can still hold the old value from a later copy.
      // The popup entry is the source of truth for this commit.
      const currentValues = { ...baseValues, ...documentValues, [key]: nextValue };
      const previousValues = { ...baseValues, ...documentValues, [key]: originalValue };

      const { updates, warnings } = recomputeChain(key, currentValues, previousValues);

      const warningByKey: Record<string, string | null> = {
        // Always written, so correcting a value back into agreement clears an earlier flag
        // instead of leaving it stuck on amber.
        [key]: warnings.find(w => w.key === key)?.message || null,
      };

      // One mark-preserving transaction updates the clicked value and every duplicate.
      applyChainUpdates(editor, updates, warningByKey);
      // The sentence states the pressure the chain assumed, so a RAP retyped in the table
      // has to reach the dropdown as well.
      if (key === 'RAP') {
        syncRapPhrase(editor, nextValue);
      }
      chainValuesRef.current = collectChainSnapshot(editor);

      if (Object.keys(updates).length > 0 || warnings.length > 0) {
        setChainNotice({
          changed: Object.keys(updates).map(k => CHAIN_FIELDS[k]?.label || k),
          warnings: warnings.map(w => w.message),
        });
      } else {
        setChainNotice(null);
      }

      runWordingRules(editor);
    } finally {
      chainWritingRef.current = false;
    }

    onChangedRef.current?.();

    setTimeout(() => {
      editor.view.dom.scrollTop = scrollPosition;
    }, 0);

    closeChainPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainSlot, chainDraft, closeChainPopup, runWordingRules]);

  /**
   * Run the chain from whatever the clinician just typed.
   *
   * Driven by the document itself: re-mark anything that came loose, read the current
   * values, and see which single one moved since last time. Only a SINGLE changed value
   * triggers a recompute — a paste, an undo or a reload moves many at once and there is no
   * sensible "source" to chain from, so those resync the snapshot silently.
   */
  const runChainFromDocument = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed || !canWriteRef.current) {
      return;
    }

    chainWritingRef.current = true;
    let changed = false;
    try {
      // Re-mark first: retyping over a value can take its mark with it, and an unmarked
      // value is invisible to the chain.
      backfillChainMarks(editor, { fillEmpty: true });
      backfillAortaSlot(editor);
      backfillTricuspidPressure(editor, { fillEmpty: true });

      const previous = chainValuesRef.current;
      if (!previous) {
        chainValuesRef.current = collectChainSnapshot(editor); // first pass, nothing to compare
        // The wording rules read the measurements, not an edit, so they still apply on the
        // very first pass after a report opens.
        changed = runWordingRules(editor);
        return;
      }

      // Occurrence-aware: a field printed in several places must react to an edit made to
      // ANY of its copies, not just the first one in the document.
      const edited = findEditedChainValue(editor, previous);
      if (!edited) {
        chainValuesRef.current = collectChainSnapshot(editor);
        changed = runWordingRules(editor);
        return;
      }

      const editedKey = edited.key;
      const baseValues = { ...(seedRef.current || {}) };
      const { updates, warnings } = recomputeChain(
        editedKey,
        { ...baseValues, ...previous, [editedKey]: edited.value },
        { ...baseValues, ...previous }
      );

      const warningByKey: Record<string, string | null> = {
        [editedKey]: warnings.find(w => w.key === editedKey)?.message || null,
      };

      applyChainUpdates(editor, updates, warningByKey);
      if (editedKey === 'RAP') {
        syncRapPhrase(editor, String(edited.value));
      }
      chainValuesRef.current = collectChainSnapshot(editor);

      if (Object.keys(updates).length > 0 || warnings.length > 0) {
        setChainNotice({
          changed: Object.keys(updates).map(k => CHAIN_FIELDS[k]?.label || k),
          warnings: warnings.map(w => w.message),
        });
      }

      const wordingChanged = runWordingRules(editor);
      changed = Object.keys(updates).length > 0 || wordingChanged;
    } finally {
      chainWritingRef.current = false;
    }

    if (changed) {
      onChangedRef.current?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runWordingRules]);

  /**
   * Mark the chained values as soon as a report is on screen. Everything saved before this
   * feature existed is plain text, so without this pass the chain would only ever work on
   * brand-new reports — which is exactly the state the reports page was in.
   *
   * `documentKey` changing means a different report (or a fresh load) is in the editor, so
   * the snapshot and the phrase baseline are rebuilt from scratch.
   */
  const primeDocument = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed) {
      return;
    }
    chainWritingRef.current = true;
    try {
      // `fillEmpty` only where the engine may write: the placeholder is real text, and a
      // Finalized report must not gain characters just by being opened.
      backfillChainMarks(editor, { fillEmpty: canWriteRef.current });
      // The aorta sentence has no editable span, so its slot is made before the
      // wording rules look for it.
      backfillAortaSlot(editor);
      // The tricuspid sentence prints RVSP with no anchor of its own, so it is given one
      // here and joins the chain like any other measurement.
      backfillTricuspidPressure(editor, { fillEmpty: canWriteRef.current });
      chainValuesRef.current = collectChainSnapshot(editor);
      // No phrase baseline is taken. Ownership is recorded on the spans and saved with
      // the report, so it cannot be lost between one sitting and the next.
    } finally {
      chainWritingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A right atrial pressure picked from the dropdown in the tricuspid sentence.
   *
   * The dropdown is a phrase slot, so the page's phrase handler owns the click — but the
   * value it carries is a measurement, and only the chain may write measurements. Kept
   * here rather than in the page so it goes through the same gate as every other chain
   * write, keeps the value snapshot in step, and surfaces the same notice.
   *
   * Returns true when the click was on that dropdown, so the caller knows it was handled.
   */
  const applyRapPick = useCallback((spanFrom: number, phrase: string): boolean => {
    const editor = editorRef.current;
    if (!editor || !canWriteRef.current) {
      return false;
    }
    if (!isRapPhrasePosition(editor, spanFrom)) {
      return false;
    }

    chainWritingRef.current = true;
    try {
      const { changed, warnings } = applyRapFromPhrase(editor, phrase, seedRef.current || {});
      // The sentence and the table now hold the same pressure; the snapshot has to agree
      // or the next document-driven pass reads this as an edit nobody made.
      chainValuesRef.current = collectChainSnapshot(editor);
      if (changed.length > 0 || warnings.length > 0) {
        setChainNotice({ changed, warnings });
      }
      runWordingRules(editor);
    } finally {
      chainWritingRef.current = false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runWordingRules]);

  // Close the popup on a click anywhere else, matching the phrase dropdown's behaviour.
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        !event.target.closest('.chain-field-popup') &&
        !event.target.closest('span[data-chain-field]')
      ) {
        setChainPosition(null);
        setChainSlot(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return {
    chainSlot,
    chainPosition,
    chainDraft,
    setChainDraft,
    chainNotice,
    dismissChainNotice: () => setChainNotice(null),
    openChainField,
    applyRapPick,
    commitChainField,
    closeChainPopup,
    runChainFromDocument,
    primeDocument,
    /** True while the engine is writing. Keep these writes out of the dirty flag. */
    chainWritingRef,
  };
}
